import { insightsIntl } from "../strings/intl";
import type {
  AdditionalCostsData,
  ReferencedChartTabProps,
  LegacyTableColumn,
  LegacyTableRow,
} from "@wayflyer/flyui-dataviz";
import { MetricReferenceDirection } from "@wayflyer/flyui-dataviz";
// eslint-disable-next-line no-restricted-imports
import moment, { type Moment } from "moment";
import { roundToNumDecimals } from "shared-utils";

import { xDomainSteps, xFormatDate } from "../utils/custom-time-range-helpers";
import type { Align, ReferenceData } from "../utils/metrics";
import { hasAdditionalCosts, includeMetricDecimal } from "../utils/metrics";
import type { CustomTimeRange, TemporalResolution } from "../utils/types";
import { TemporalResolutions } from "../utils/types";
import type { DashboardMetadata } from "../utils/fetch-dashboard-metadata";
import { MetricTypes } from "../autogen/metrics";
import type { DashboardOutput } from "../utils/fetch-api";
import { formatValue } from "../strings/type-formatting";
import type { ProfitabilityDataPoint } from "../components/profitability-chart";
import { overviewKpiData, type OverviewKpiMetric } from "./kpi-data";

export type ChartsAndTableData = {
  chartData: ProfitabilityDataPoint[];
  tableData: {
    columns: LegacyTableColumn[];
    data: LegacyTableRow[];
  };
};

const getInsightsOverviewTableColumns = (
  tabs: ReferencedChartTabProps[],
  additionalCosts: AdditionalCostsData
): LegacyTableColumn[] => {
  // add the three columns after the two sales columns, or the beginning if none exist
  let additionalColumnsStartIndex = 0;
  const hasGrossRevenueLessDiscountsPlusShippingPlusTax = tabs.find(
    (tab) =>
      tab.metricType === MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX ||
      tab.metricType === MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX
  );
  const columns: LegacyTableColumn[] = tabs.map((tab, tabIndex) => {
    switch (tab.metricType) {
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX:
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX:
        if (!hasGrossRevenueLessDiscountsPlusShippingPlusTax) {
          additionalColumnsStartIndex = tabIndex + 1;
        }
        break;
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX:
        additionalColumnsStartIndex = tabIndex + 1;
        break;
    }
    return {
      name: tab.title,
      id: tab.metricType,
      align: "right" as Align,
    };
  });

  if (hasAdditionalCosts(additionalCosts)) {
    // add the profit
    columns.splice(additionalColumnsStartIndex, 0, {
      name: insightsIntl.formatMessage({ id: "Net Profit", defaultMessage: "Net Profit" }),
      id: MetricTypes.PROFIT,
      align: "right" as Align,
    });

    if (additionalCosts.opex) {
      // add the OpEx
      columns.splice(additionalColumnsStartIndex, 0, {
        name: insightsIntl.formatMessage({ id: "Operational expenditure", defaultMessage: "Operational expenditure" }),
        id: MetricTypes.ADDITIONAL_OPEX,
        align: "right" as Align,
      });
    }

    if (additionalCosts.cogs || additionalCosts.shipping) {
      // add the CoGS and Shipping
      columns.splice(additionalColumnsStartIndex, 0, {
        name: insightsIntl.formatMessage({
          id: "Cost of goods sold and Shipping cost",
          defaultMessage: "Cost of goods sold and Shipping cost",
        }),
        id: MetricTypes.ADDITIONAL_COGS_AND_SHIPPING,
        align: "right" as Align,
      });
    }
  }

  // add the time resolution column to the start
  columns.splice(0, 0, {
    name: insightsIntl.formatMessage({ id: "Period", defaultMessage: "Period" }),
    id: "period",
    align: "left" as Align,
  });

  return columns;
};

export const getInsightsOverviewChartAndTableData = (
  timeRange: CustomTimeRange,
  data: DashboardOutput,
  temporalResolution: TemporalResolution,
  inputTabs: ReferencedChartTabProps[],
  tableTabs: ReferencedChartTabProps[],
  additionalCosts: AdditionalCostsData
): ChartsAndTableData => {
  const steps = xDomainSteps(timeRange.start, timeRange.end, temporalResolution);
  const todayDate = moment().format();

  const dataByDay = {};
  data.data.map((row) => {
    dataByDay[moment(row["day"]).valueOf()] = row;
  });

  const chartsAndTableData: ChartsAndTableData = steps.reduce(
    (multiLineData: ChartsAndTableData, step: Moment) => {
      const day = xFormatDate(step, temporalResolution, steps[0], steps[steps.length - 1]);
      const dayRaw = step.format("YYYY-MM-DD");
      // get the Net Quantity for use in the other datapoints calculation
      const netQuantityDataPoints = getInsightsDataPointsPerDay(
        step,
        temporalResolution,
        dataByDay,
        MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY
      );
      multiLineData.chartData.push({
        day,
        dayRaw,
        ...inputTabs.reduce(
          (linesData: ProfitabilityDataPoint, tab) => {
            linesData = {
              ...linesData,
              ...getInsightsDataPointsPerDay(step, temporalResolution, dataByDay, tab.metricType as MetricTypes),
            };
            return linesData;
          },
          { ...netQuantityDataPoints } as ProfitabilityDataPoint
        ),
      });

      if (
        multiLineData &&
        multiLineData.chartData.length > 0 &&
        !hasAdditionalCosts(additionalCosts) &&
        dayRaw <= todayDate
      ) {
        multiLineData.tableData.data.push(
          getInsightsOverviewTableDataRow(
            multiLineData.chartData.slice(-1).pop() ?? multiLineData.chartData[multiLineData.chartData.length - 1],
            tableTabs,
            data.currencyCode,
            data.metrics
          )
        );
      }
      return multiLineData;
    },
    {
      chartData: [],
      tableData: {
        columns: getInsightsOverviewTableColumns(tableTabs, additionalCosts),
        data: [],
      },
    }
  );
  return addAdditionalCostsData(
    temporalResolution,
    chartsAndTableData,
    tableTabs,
    additionalCosts,
    data.currencyCode,
    data.metrics
  );
};

const getInsightsOverviewTableDataRow = (
  chartDataPoint: ProfitabilityDataPoint,
  tabs: ReferencedChartTabProps[],
  currencyCode: string,
  metrics,
  additionalCostsPerPoint?: AdditionalCostsData
): LegacyTableRow => {
  const dataRow = tabs.reduce((rowData: LegacyTableRow, tab) => {
    const dataValueRaw = chartDataPoint[`thisYears${getMetricDataPointKey(tab.metricType as MetricTypes)}Value`];

    rowData[`${tab.metricType}`] =
      dataValueRaw == null
        ? "-"
        : tab.metricType === MetricTypes.ROAS
          ? formatValue({
              value: dataValueRaw.toFixed(1),
              metricType: { type: "RATE" },
              withDecimal: includeMetricDecimal(MetricTypes.ROAS),
            })
          : formatValue({
              value: dataValueRaw,
              metricType: {
                type: "CURRENCY",
                currencyCode: currencyCode,
              },
              withDecimal: includeMetricDecimal(tab.metricType as MetricTypes),
            });

    rowData[`sort_${tab.metricType}`] = dataValueRaw == null ? "-" : dataValueRaw;
    return rowData;
  }, {} as LegacyTableRow);

  // add the period (day, week, month) cell
  dataRow["period"] = chartDataPoint.day;
  if (chartDataPoint.dayRaw) {
    dataRow["sort_period"] = chartDataPoint.dayRaw;
  }

  if (additionalCostsPerPoint && hasAdditionalCosts(additionalCostsPerPoint)) {
    // we add the profit
    dataRow[MetricTypes.PROFIT] = formatValue({
      value: chartDataPoint[`thisYears${getMetricDataPointKey(MetricTypes.PROFIT)}Value`],
      metricType: {
        type: "CURRENCY",
        currencyCode: currencyCode,
      },
      withDecimal: includeMetricDecimal(MetricTypes.PROFIT),
    });
    dataRow[`sort_${MetricTypes.PROFIT}`] =
      chartDataPoint[`thisYears${getMetricDataPointKey(MetricTypes.PROFIT)}Value`];
    // add the CoGS + shipping
    dataRow[MetricTypes.ADDITIONAL_COGS_AND_SHIPPING] = formatValue({
      value: (additionalCostsPerPoint.cogs + additionalCostsPerPoint.shipping).toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: currencyCode,
      },
      withDecimal: includeMetricDecimal(MetricTypes.ADDITIONAL_COGS_AND_SHIPPING),
    });
    dataRow[`sort_${MetricTypes.ADDITIONAL_COGS_AND_SHIPPING}`] =
      additionalCostsPerPoint.cogs + additionalCostsPerPoint.shipping;
    // add the OpEx
    dataRow[MetricTypes.ADDITIONAL_OPEX] = formatValue({
      value: additionalCostsPerPoint.opex.toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: currencyCode,
      },
      withDecimal: includeMetricDecimal(MetricTypes.ADDITIONAL_OPEX),
    });
    dataRow[`sort_${MetricTypes.ADDITIONAL_OPEX}`] = additionalCostsPerPoint.opex;
  }
  return dataRow;
};

const getMonthlyAverageCost = (monthlyAverage: number, temporalResolution: TemporalResolution, dateStr: string) => {
  const pointDate = moment(dateStr);
  const numDaysInYear = pointDate.isLeapYear() ? 366 : 365;
  const isCurrentMonth = pointDate.year() === moment().year() && pointDate.month() === moment().month();
  const amountPerDay = (monthlyAverage * 12) / numDaysInYear;
  switch (temporalResolution.resolution) {
    case "MONTHLY":
      if (isCurrentMonth) {
        return amountPerDay * moment().date();
      }
      return monthlyAverage;
    case "WEEKLY": {
      const amountPerWeek = amountPerDay * 7;
      if (isCurrentMonth && pointDate.week() === moment().week()) {
        return amountPerWeek * (moment().day() / 7);
      }
      return amountPerWeek;
    }
    case "DAILY":
      return amountPerDay;
  }
  return monthlyAverage;
};

/*
 * If additional costs have been entered using the form we need to update the data points that are affected for the charts and the table.
 * This includes adding a profit datapoint for each step if any additional costs were entered
 * and updating marketing, cac and roas if additional marketing costs were entered,
 */
const addAdditionalCostsData = (
  temporalResolution: TemporalResolution,
  chartsAndTableData: ChartsAndTableData,
  tableTabs: ReferencedChartTabProps[],
  additionalCosts: AdditionalCostsData,
  currencyCode: string,
  metrics
): ChartsAndTableData => {
  if (!hasAdditionalCosts(additionalCosts) || chartsAndTableData.chartData.length === 0) {
    return chartsAndTableData;
  }
  const updatedTableDataRows: LegacyTableRow[] = [];
  const todayDate = moment().format("YYYY-MM-DD");
  const updatedChartData = chartsAndTableData.chartData.map((_multiLineData) => {
    const multiLineData = _multiLineData as any;
    // Costs are monthly averages, so we need to find out the amount per data point
    const additionalMarketingCost = roundToNumDecimals(
      getMonthlyAverageCost(additionalCosts.marketing, temporalResolution, multiLineData.dayRaw)
    );
    const additionalOpExCost = roundToNumDecimals(
      getMonthlyAverageCost(additionalCosts.opex, temporalResolution, multiLineData.dayRaw)
    );

    const additionalMonthlyCostsTotal = additionalMarketingCost + additionalOpExCost;

    // use sales data if exists otherwise use sales ex returns
    const salesData =
      (multiLineData.thisYearsSalesValue == null
        ? multiLineData.thisYearsSalesIncReturnsValue
        : multiLineData.thisYearsSalesValue) || 0;
    const salesDataPrevious =
      (multiLineData.thisYearsSalesPreviousValue == null
        ? multiLineData.thisYearsSalesIncReturnsPreviousValue
        : multiLineData.thisYearsSalesPreviousValue) || 0;
    const salesDataLastYear =
      (multiLineData.lastYearsSalesValue == null
        ? multiLineData.lastYearsSalesIncReturnsValue
        : multiLineData.lastYearsSalesValue) || 0;

    const thisYearsMarketingValue = multiLineData.thisYearsMarketingValue || 0;
    const thisYearsMarketingPreviousValue = multiLineData.thisYearsMarketingPreviousValue || 0;
    const lastYearsMarketingValue = multiLineData.lastYearsMarketingValue || 0;

    const thisYearsMarketingValueUpdated = roundToNumDecimals(thisYearsMarketingValue + additionalMarketingCost);
    const thisYearsMarketingPreviousValueUpdated = roundToNumDecimals(
      thisYearsMarketingPreviousValue + additionalMarketingCost
    );
    const lastYearsMarketingValueUpdated = roundToNumDecimals(lastYearsMarketingValue + additionalMarketingCost);

    const thisYearsOrderCountValue = multiLineData.thisYearsOrderCountValue || 0;
    const thisYearsOrderCountPreviousValue = multiLineData.thisYearsOrderCountPreviousValue || 0;
    const lastYearsOrderCountValue = multiLineData.lastYearsOrderCountValue || 0;

    const thisYearsAOVValue = thisYearsOrderCountValue > 0 ? salesData / thisYearsOrderCountValue : null;
    const thisYearsAOVPreviousValue =
      thisYearsOrderCountPreviousValue > 0 ? salesDataPrevious / thisYearsOrderCountPreviousValue : null;
    const lastYearsAOVValue = lastYearsOrderCountValue > 0 ? salesDataLastYear / lastYearsOrderCountValue : null;

    const thisYearsNewCustomersValue = multiLineData.thisYearsNewCustomersValue || 0;
    const thisYearsNewCustomersPreviousValue = multiLineData.thisYearsNewCustomersPreviousValue || 0;
    const lastYearsNewCustomersValue = multiLineData.lastYearsNewCustomersValue || 0;

    // cac = marketing / num customers
    const thisYearsCACValue =
      thisYearsNewCustomersValue > 0 ? thisYearsMarketingValueUpdated / thisYearsNewCustomersValue : null;
    const thisYearsCACPreviousValue =
      thisYearsNewCustomersPreviousValue > 0
        ? thisYearsMarketingPreviousValueUpdated / thisYearsNewCustomersPreviousValue
        : null;
    const lastYearsCACValue =
      lastYearsNewCustomersValue > 0 ? lastYearsMarketingValueUpdated / lastYearsNewCustomersValue : null;
    // roas = sales / marketing
    const thisYearsROASValue =
      thisYearsMarketingValueUpdated > 0 ? roundToNumDecimals(salesData / thisYearsMarketingValueUpdated) : 0;
    const thisYearsROASPreviousValue =
      thisYearsMarketingPreviousValueUpdated > 0
        ? roundToNumDecimals(salesDataPrevious / thisYearsMarketingPreviousValueUpdated)
        : 0;
    const lastYearsROASValue =
      lastYearsMarketingValueUpdated > 0 ? roundToNumDecimals(salesDataLastYear / lastYearsMarketingValueUpdated) : 0;

    // multiply the additional shipping cost per item by the number of items sold at this data point
    const additionalShippingCostThisYear = roundToNumDecimals(
      additionalCosts.shipping * (multiLineData.thisYearsNetQuantityValue || 0)
    );
    const additionalShippingCostThisYearPrevious = roundToNumDecimals(
      additionalCosts.shipping * (multiLineData.thisYearsNetQuantityPreviousValue || 0)
    );
    const additionalShippingCostLastYear = roundToNumDecimals(
      additionalCosts.shipping * (multiLineData.lastYearsNetQuantityValue || 0)
    );
    const additionalCoGSCostThisYear = roundToNumDecimals(
      additionalCosts.cogs * (multiLineData.thisYearsNetQuantityValue || 0)
    );
    const additionalCoGSCostThisYearPrevious = roundToNumDecimals(
      additionalCosts.cogs * (multiLineData.thisYearsNetQuantityPreviousValue || 0)
    );
    const additionalCoGSCostLastYear = roundToNumDecimals(
      additionalCosts.cogs * (multiLineData.lastYearsNetQuantityValue || 0)
    );
    const thisYearsProfit =
      multiLineData.dayRaw <= todayDate
        ? {
            thisYearsProfitValue: roundToNumDecimals(
              salesData -
                thisYearsMarketingValue -
                additionalMonthlyCostsTotal -
                additionalShippingCostThisYear -
                additionalCoGSCostThisYear
            ),
            thisYearsProfitPreviousValue: roundToNumDecimals(
              salesDataPrevious -
                thisYearsMarketingPreviousValue -
                additionalMonthlyCostsTotal -
                additionalShippingCostThisYearPrevious -
                additionalCoGSCostThisYearPrevious
            ),
          }
        : {};
    const dataPoint = {
      ...multiLineData,
      ...thisYearsProfit,
      lastYearsProfitValue: roundToNumDecimals(
        salesDataLastYear -
          lastYearsMarketingValue -
          additionalMonthlyCostsTotal -
          additionalShippingCostLastYear -
          additionalCoGSCostLastYear
      ),
      thisYearsMarketingValue:
        thisYearsMarketingValueUpdated === 0 || multiLineData.dayRaw > todayDate
          ? null
          : thisYearsMarketingValueUpdated,
      thisYearsMarketingPreviousValue:
        thisYearsMarketingPreviousValueUpdated === 0 ? null : thisYearsMarketingPreviousValueUpdated,
      lastYearsMarketingValue: lastYearsMarketingValueUpdated === 0 ? null : lastYearsMarketingValueUpdated,
      thisYearsCACValue: thisYearsCACValue,
      thisYearsCACPreviousValue: thisYearsCACPreviousValue,
      lastYearsCACValue: lastYearsCACValue,
      thisYearsAOVValue: thisYearsAOVValue,
      thisYearsAOVPreviousValue: thisYearsAOVPreviousValue,
      lastYearsAOVValue: lastYearsAOVValue,
      thisYearsROASValue:
        thisYearsROASValue === 0 && multiLineData.thisYearsROASValue === null ? null : thisYearsROASValue,
      thisYearsROASPreviousValue,
      lastYearsROASValue,
    };
    if (multiLineData.dayRaw <= todayDate) {
      updatedTableDataRows.push(
        getInsightsOverviewTableDataRow(dataPoint, tableTabs, currencyCode, metrics, {
          shipping: additionalShippingCostThisYear,
          cogs: additionalCoGSCostThisYear,
          marketing: additionalMarketingCost,
          opex: additionalOpExCost,
        })
      );
    }
    return dataPoint;
  });

  return {
    chartData: updatedChartData,
    tableData: {
      columns: chartsAndTableData.tableData.columns,
      data: updatedTableDataRows,
    },
  };
};

const getDataPoint = (day: Moment, metricType: MetricTypes, pointsByDay) => {
  return pointsByDay?.[day.valueOf()]?.[metricType];
};

const getInsightsDataPointsPerDay = (
  day: Moment,
  temporalResolution: TemporalResolution,
  pointsByDay,
  metricType: MetricTypes
): Partial<ProfitabilityDataPoint> => {
  const thisYearValue = getDataPoint(day, metricType, pointsByDay);
  const previousThisYearValue = getDataPoint(
    day.clone().subtract(1, temporalResolution.duration),
    metricType,
    pointsByDay
  );
  let sameDayLastYear = day.clone().subtract(1, "year");
  if (temporalResolution.resolution === TemporalResolutions.WEEKLY.resolution) {
    sameDayLastYear = day.clone().subtract(1, "year").startOf("week");
  }
  const lastYearValue = getDataPoint(sameDayLastYear, metricType, pointsByDay);
  const returnData = {};
  returnData[`thisYears${getMetricDataPointKey(metricType)}Value`] = thisYearValue ? parseFloat(thisYearValue) : null;
  returnData[`thisYears${getMetricDataPointKey(metricType)}PreviousValue`] = previousThisYearValue
    ? parseFloat(previousThisYearValue)
    : null;
  returnData[`lastYears${getMetricDataPointKey(metricType)}Value`] = lastYearValue ? parseFloat(lastYearValue) : null;
  return returnData;
};

const getMetricDataPointKey = (metricType: MetricTypes): string => {
  switch (metricType) {
    case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX:
      return "SalesIncReturns";
    case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX:
    case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX:
      return "Sales";
    case MetricTypes.MARKETING_SPEND:
      return "Marketing";
    case MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY:
      return "NetQuantity";
    case MetricTypes.TOTAL_UNIQUE_NEW_CUSTOMER_COUNT:
      return "NewCustomers";
    case MetricTypes.TOTAL_ORDER_QUANTITY:
      return "OrderCount";
    default:
      return metricType;
  }
};

const getMonthlyAverageCostFromTimeRange = (monthlyAverage: number, timeRange) => {
  const days = timeRange.end.diff(timeRange.start, "days") + 1;
  const dailyAverage = (12 * monthlyAverage) / 365; // Leap year unaware
  return dailyAverage * days;
};

const updateProfitTab = (
  current,
  reference,
  tabs: ReferencedChartTabProps[],
  additionalCosts: AdditionalCostsData,
  metadata: DashboardMetadata,
  currencyCode,
  timeRange
) => {
  let profitTabIndex = -1;
  let profitSales = 0;
  let profitSalesRef = 0;
  let profitMarketing = 0;
  let profitMarketingRef = 0;
  const grossRevenueLessDiscountsLessRefundsPlusShippingPlusTaxMetric = metadata.supports_refunds_by_refunded_date
    ? MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX
    : MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX;

  const hasGrossRevenueLessDiscountsLessRefundsPlusShippingPlusTax = tabs.find(
    (tab) => tab.metricType === grossRevenueLessDiscountsLessRefundsPlusShippingPlusTaxMetric
  );
  const hasProfitTab = tabs.find((tab) => tab.metricType === MetricTypes.PROFIT);
  tabs.forEach((tab, tabIndex) => {
    switch (tab.metricType) {
      case MetricTypes.PROFIT:
        profitTabIndex = tabIndex;
        break;
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX: // sales ex refunds
        profitSales = current?.[tab.metricType];
        profitSalesRef = reference?.[tab.metricType];
        if (!hasProfitTab) {
          profitTabIndex = tabIndex + 1;
        }
        break;
      case grossRevenueLessDiscountsLessRefundsPlusShippingPlusTaxMetric:
        profitSales = current?.[tab.metricType];
        profitSalesRef = reference?.[tab.metricType];
        if (!hasProfitTab) {
          profitTabIndex = tabIndex + 1;
        }
        break;
      case MetricTypes.MARKETING_SPEND:
        profitMarketing = current?.[tab.metricType];
        profitMarketingRef = reference?.[tab.metricType];
        break;
    }
  });

  // we need to add a Profit metric based on Sales - Marketing - (additionalCosts * #months)
  const sales =
    hasGrossRevenueLessDiscountsLessRefundsPlusShippingPlusTax ||
    tabs.find((tab) => tab.metricType === MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX);
  const marketing = tabs.find((tab) => tab.metricType === MetricTypes.MARKETING_SPEND);
  if (sales && marketing && profitTabIndex > -1) {
    // we add a profit tab between last of the sales tabs and their neighbour
    const additionalMonthlyCostsTotal =
      getMonthlyAverageCostFromTimeRange(additionalCosts.opex, timeRange) +
      getMonthlyAverageCostFromTimeRange(additionalCosts.marketing, timeRange);
    // multiply the additional shipping & cogs cost per item by the number of items sold
    const netQuantityCurrent = current?.[MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY];
    const netQuantityReference = reference?.[MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY];

    const additionalShippingCostThisYear = roundToNumDecimals(additionalCosts.shipping * (netQuantityCurrent || 0));
    const additionalShippingCostLastYear = roundToNumDecimals(additionalCosts.shipping * (netQuantityReference || 0));
    const additionalCoGSCostThisYear = roundToNumDecimals(additionalCosts.cogs * (netQuantityCurrent || 0));
    const additionalCoGSCostLastYear = roundToNumDecimals(additionalCosts.cogs * (netQuantityReference || 0));

    const profitCurrent = Math.round(
      profitSales -
        profitMarketing -
        additionalMonthlyCostsTotal -
        additionalShippingCostThisYear -
        additionalCoGSCostThisYear
    );
    const profitReference = Math.round(
      profitSalesRef -
        profitMarketingRef -
        additionalMonthlyCostsTotal -
        additionalShippingCostLastYear -
        additionalCoGSCostLastYear
    );

    const referenceData = getReferenceData(profitCurrent, profitReference, "up");
    const profitTab: ReferencedChartTabProps & OverviewKpiMetric = {
      title: insightsIntl.formatMessage({ id: "Net Profit", defaultMessage: "Net Profit" }),
      tooltip: insightsIntl.formatMessage({
        id: "Total sales minus marketing costs from your connected platforms and any additional costs inputted using the form below.",
        defaultMessage:
          "Total sales minus marketing costs from your connected platforms and any additional costs inputted using the form below.",
      }),
      metricType: MetricTypes.PROFIT,
      metricValue: formatValue({ value: profitCurrent.toString(), metricType: { type: "CURRENCY", currencyCode } }),
      metricReferenceValue: referenceData?.metricReferenceValue,
      metricReferenceDirection: referenceData?.metricReferenceDirection,
      metricReferenceSuccess: referenceData?.metricReferenceSuccess,
      kpi: overviewKpiData(
        profitCurrent,
        { type: "CURRENCY", currencyCode },
        undefined,
        referenceData ? ((profitCurrent - profitReference) * 100) / profitReference : undefined
      ),
    };
    tabs.splice(profitTabIndex, hasProfitTab ? 1 : 0, profitTab);
  }

  return tabs;
};

export const getReferenceData = (
  current: number,
  reference: number,
  successDirection?: string
): ReferenceData | null => {
  const delta = ((current - reference) * 100) / reference;
  if (!isNaN(delta)) {
    const invertColor: boolean = successDirection === "down";
    const deltaPositive: boolean = delta >= 0;
    const deltaZero: boolean = delta * (deltaPositive ? 1 : -1) < 0.05;
    // if delta is essentially zero & negative, multiply by -1 to remove sign.
    const deltaValueToRender = (delta * (deltaZero && !deltaPositive ? -1 : 1)).toString();
    return {
      metricReferenceSuccess: deltaPositive !== invertColor && !deltaZero,
      metricReferenceDirection: deltaZero
        ? MetricReferenceDirection.SAME
        : deltaPositive
          ? MetricReferenceDirection.UP
          : MetricReferenceDirection.DOWN,
      metricReferenceValue: deltaZero
        ? "0%"
        : formatValue({ value: deltaValueToRender, metricType: { type: "PERCENTAGE" } }),
    };
  }

  return null;
};

const getROASWithAdditionalCosts = (data, timeRange: CustomTimeRange, additionalCosts?: AdditionalCostsData) => {
  const salesValue = parseFloat(data?.[MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX]);
  const marketingValue = getMarketingWithAdditionalCosts(data, timeRange, additionalCosts);
  if (marketingValue) {
    return roundToNumDecimals(salesValue / marketingValue, 1);
  } else {
    return null;
  }
};

const getCACWithAdditionalCosts = (data, timeRange: CustomTimeRange, additionalCosts?: AdditionalCostsData) => {
  if (data?.[MetricTypes.TOTAL_UNIQUE_NEW_CUSTOMER_COUNT]) {
    return (
      getMarketingWithAdditionalCosts(data, timeRange, additionalCosts) /
      data?.[MetricTypes.TOTAL_UNIQUE_NEW_CUSTOMER_COUNT]
    );
  } else {
    return null;
  }
};

const getMarketingWithAdditionalCosts = (data, timeRange: CustomTimeRange, additionalCosts?: AdditionalCostsData) => {
  const marketing = data?.[MetricTypes.MARKETING_SPEND];
  if (!additionalCosts?.marketing) {
    return marketing;
  }
  return marketing + getMonthlyAverageCostFromTimeRange(additionalCosts.marketing, timeRange);
};

export const aggregateOverviewSummaryData = (
  current: DashboardOutput,
  reference: DashboardOutput,
  timeRange: CustomTimeRange,
  tabs: ReferencedChartTabProps[],
  additionalCosts: AdditionalCostsData,
  metadata: DashboardMetadata
) => {
  const deriveMetric = (metricType, data) => {
    if (metricType === MetricTypes.AOV) {
      const revenue = data?.[MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX] || 0;
      const orderQuantity = data?.[MetricTypes.TOTAL_ORDER_QUANTITY] || 0;
      return orderQuantity ? revenue / orderQuantity : 0;
    } else if (metricType === MetricTypes.ROAS) {
      return getROASWithAdditionalCosts(data, timeRange, additionalCosts);
    } else if (metricType === MetricTypes.CAC) {
      return getCACWithAdditionalCosts(data, timeRange, additionalCosts);
    } else if (metricType === MetricTypes.MARKETING_SPEND) {
      return getMarketingWithAdditionalCosts(data, timeRange, additionalCosts);
    }
    return data?.[metricType] || "";
  };

  const tabsData = tabs.map((sourceTab) => {
    const tab: ReferencedChartTabProps & OverviewKpiMetric = sourceTab;
    const metric = current.metrics.find((metric) => metric.name === tab.metricType);

    const metricValue = deriveMetric(tab.metricType, current.summary);
    tab.metricValue =
      metricValue == null
        ? undefined
        : formatValue({
            value: metricValue.toString(),
            metricType: metric,
            withDecimal: includeMetricDecimal(tab.metricType as MetricTypes),
          });
    const metricReferenceValue = deriveMetric(tab.metricType, reference.summary);
    tab.metricReferenceValue =
      metricReferenceValue == null
        ? undefined
        : formatValue({
            value: metricReferenceValue.toString(),
            metricType: metric,
            withDecimal: includeMetricDecimal(tab.metricType as MetricTypes),
          });
    const referenceData = getReferenceData(metricValue, metricReferenceValue, metric?.success_direction);
    tab.kpi = overviewKpiData(
      metricValue === "" && current.summary[tab.metricType] === 0 ? 0 : metricValue,
      metric,
      includeMetricDecimal(tab.metricType as MetricTypes),
      referenceData ? ((metricValue - metricReferenceValue) * 100) / metricReferenceValue : undefined
    );
    if (referenceData) {
      tab.metricReferenceSuccess = referenceData.metricReferenceSuccess;
      tab.metricReferenceDirection = referenceData.metricReferenceDirection;
      tab.metricReferenceValue = referenceData.metricReferenceValue;
    }
    return tab;
  });

  if (hasAdditionalCosts(additionalCosts)) {
    return updateProfitTab(
      current.summary,
      reference.summary,
      tabsData,
      additionalCosts,
      metadata,
      current.currencyCode,
      timeRange
    );
  }

  return tabsData;
};
